// AdminForgotPassword — code-based recovery (6-digit code via e-mail) with
// optional fallback toward the recovery e-mail and a WhatsApp escape hatch.
import React, { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, Loader2, AlertCircle, CheckCircle2, MessageCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WHATSAPP_URL = `https://wa.me/5581981739909?text=${encodeURIComponent(
  'Olá, preciso recuperar minha senha do painel Tô de Olho',
)}`;

const AdminForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [usarRec, setUsarRec] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/admin/auth/forgot-password`, {
        email: email.trim().toLowerCase(),
        usar_recuperacao: usarRec,
      });
      setDone(true);
      // After 1.5s redirect to reset page with email pre-filled
      setTimeout(() => {
        navigate(`/admin/reset-password?email=${encodeURIComponent(email.trim().toLowerCase())}`, { replace: true });
      }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Não foi possível processar o pedido. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-lg shadow-md border border-[#E2E8F0] p-6"
           data-testid="admin-forgot-page">
        <div className="text-center mb-5">
          <div className="w-12 h-12 mx-auto bg-[#1E3A5F] rounded-md flex items-center justify-center mb-3">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-xl font-black text-[#0F1C2E]">Recuperar senha</h1>
          <p className="text-xs text-[#475569] mt-1">Painel administrativo · Tô de Olho</p>
        </div>

        {done ? (
          <div className="space-y-3" data-testid="admin-forgot-done">
            <div className="flex items-start gap-2 p-3 rounded bg-[#DCFCE7] border border-[#BBF7D0] text-[#006030] text-xs">
              <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                Se o e-mail estiver cadastrado, enviamos um <strong>código de 6 dígitos</strong>. Ele expira em 15 minutos.
                Redirecionando...
              </span>
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div className="flex items-start gap-2 p-2.5 mb-3 rounded bg-[#FEE2E2] border border-[#FECACA] text-[#7F1D1D] text-xs"
                   data-testid="admin-forgot-error">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{error}</span>
              </div>
            )}
            <form onSubmit={onSubmit} className="space-y-3">
              <label className="block">
                <span className="text-[11px] font-medium text-[#475569] uppercase tracking-wider">E-mail do admin</span>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  className="mt-1 w-full px-3 py-2 border border-[#E2E8F0] rounded text-sm focus:outline-none focus:border-[#1E3A5F]"
                  data-testid="admin-forgot-email"
                />
              </label>
              <label className="flex items-start gap-2 text-[11px] text-[#475569]">
                <input
                  type="checkbox"
                  checked={usarRec}
                  onChange={(e) => setUsarRec(e.target.checked)}
                  className="mt-0.5"
                  data-testid="admin-forgot-usar-rec"
                />
                <span>
                  Enviar para o meu <strong>e-mail de recuperação</strong> (se cadastrado e verificado).
                </span>
              </label>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1E3A5F] hover:bg-[#16304b] text-white font-bold py-2 rounded text-sm disabled:opacity-60 flex items-center justify-center"
                data-testid="admin-forgot-submit"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Enviar código por e-mail'}
              </button>
            </form>

            <div className="my-4 flex items-center gap-2 text-[10px] text-[#94A3B8]">
              <span className="flex-1 h-px bg-[#E2E8F0]" />
              <span>OU</span>
              <span className="flex-1 h-px bg-[#E2E8F0]" />
            </div>

            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 border border-[#25D366] text-[#0F1C2E] hover:bg-[#25D366]/10 font-bold py-2 rounded text-sm"
              data-testid="admin-forgot-whatsapp"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              Recuperar via WhatsApp
            </a>
            <p className="text-[10px] text-[#94A3B8] text-center mt-1">Atendimento manual em horário comercial.</p>

            <Link to="/admin/login"
                  className="mt-4 block text-center text-xs text-[#475569] hover:text-[#1E3A5F]"
                  data-testid="admin-forgot-back">
              Voltar para o login
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminForgotPassword;
